import React, { useState, useMemo } from "react";
import { useTable, usePagination, useSortBy, useExpanded, useResizeColumns, useFlexLayout } from "react-table";
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import { IMPORT_LIST_COLUMNS } from "@/containers/Import/components/ImportListColumns";
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import { reducer, useEffectToDispatch, fetchApiData, initialState, Sorting } from "@/shared/apiTableHelper";
import { Col, Row, Button } from 'reactstrap';
import ProgramsHierarchy from '@/shared/components/ProgramsHierarchy';
import { connect } from 'react-redux';

const queryClient = new QueryClient();

const ProgramImportFilter = ({ onClickFilterCallback }) => {
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [keyword, setKeyword] = useState('');

  const onClickFilter = (e, reset = false) => {
    e.preventDefault();
    if (reset) {
      setKeyword('');
      setSelectedPrograms([]);
      onClickFilterCallback('', []);
    } else {
      onClickFilterCallback(keyword, selectedPrograms);
    }
  };

  return (
    <Row className="table-filter-form form">
      <Col md={8} lg={8} sm={8} className="table-filter-form-fields">
        <div>
          <div className="table-filter-form-col table-filter-form-col1 float-filter" style={{ paddingTop: 4 }}>
            <div className="form__form-group">
              <div className="form__form-group-field">
                <div className="form__form-group-row">
                  <ProgramsHierarchy
                    selectedPrograms={selectedPrograms}
                    setSelectedPrograms={setSelectedPrograms}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="table-filter-form-col table-filter-form-col3 float-filter">
            <div className="form__form-group">
              <span className="form__form-group-label" style={{ whiteSpace: 'nowrap' }}>File Name:</span>
              <div className="form__form-group-field">
                <div className="form__form-group-row">
                  <input
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    type="text"
                    placeholder="Search by File Name"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Col>
      <Col className="align-items-center max-height-32px pl-1">
        <Button onClick={(e) => onClickFilter(e)} className="btn btn-sm btn-primary" color="#ffffff">Filter</Button>
        <Button onClick={(e) => onClickFilter(e, true)} className="btn btn-sm btn-primary" color="#ffffff">Reset</Button>
      </Col>
    </Row>
  );
};

const DataTable = ({ organization, program }) => {
  const orgId = program?.organization_id || program?.organization?.id || organization?.id;

  const [filter, setFilter] = useState({ keyword: '', programs: [] });
  const [useFilter, setUseFilter] = useState(false);

  const onClickFilterCallback = (keyword, programs) => {
    setFilter({ keyword, programs });
    setUseFilter(true);
  };

  const columns = useMemo(() => [...IMPORT_LIST_COLUMNS], []);

  const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy }, dispatch] =
    React.useReducer(reducer, initialState);

  const { isLoading, error, data, isSuccess } = useQuery(
    ['program-import', orgId, program?.id, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy],
    () =>
      fetchApiData({
        url: `/organization/${orgId}/program/${program.id}/import`,
        page: queryPageIndex,
        size: queryPageSize,
        filter: queryPageFilter,
        sortby: queryPageSortBy,
      }),
    {
      enabled: !!orgId && !!program?.id,
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  const totalPageCount = Math.ceil(totalCount / queryPageSize);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data: data?.results ?? [],
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
      manualPagination: true,
      pageCount: data ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useFlexLayout
  );

  const manualPageSize = [];
  useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter });

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p>Error: {JSON.stringify(error)}</p>;

  return (
    <>
      <div className="table react-table">
        <div className="action-panel">
          <Row className="mx-0">
            <Col>
              <ProgramImportFilter
                onClickFilterCallback={onClickFilterCallback}
              />
            </Col>
          </Row>
        </div>

        <table {...getTableProps()} className="table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    {column.isSorted ? <Sorting column={column} /> : ''}
                    <div
                      {...column.getResizerProps()}
                      className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                    />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="table table--bordered" {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const subCount = (row.id.match(/\./g) || []).length;
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    const paddingLeft = subCount * 20;
                    return (
                      <td {...cell.getCellProps()}>
                        <span style={cell.column.Header === '#' ? { paddingLeft: `${paddingLeft}px` } : null}>
                          {cell.render('Cell')}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && (
        <>
          <ReactTablePagination
            page={page}
            gotoPage={gotoPage}
            previousPage={previousPage}
            nextPage={nextPage}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageOptions={pageOptions}
            pageSize={pageSize}
            pageIndex={pageIndex}
            pageCount={pageCount}
            setPageSize={setPageSize}
            manualPageSize={manualPageSize}
            dataLength={totalCount}
          />
          <div className="pagination justify-content-end mt-2">
            <span>
              Go to page:{' '}
              <input
                type="number"
                value={pageIndex + 1}
                onChange={(e) => {
                  const p = e.target.value ? Number(e.target.value) - 1 : 0;
                  gotoPage(p);
                }}
                style={{ width: '100px' }}
              />
            </span>{' '}
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {[10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>Show {size}</option>
              ))}
            </select>
          </div>
        </>
      )}
    </>
  );
};

const ProgramImportListDataTable = ({ organization, program, auth }) => {
  if (!organization || !program) return 'Loading...';
  return (
    <QueryClientProvider client={queryClient}>
      <DataTable organization={organization} program={program} auth={auth} />
    </QueryClientProvider>
  );
};

export default connect((state) => ({
  auth: state.auth,
}))(ProgramImportListDataTable);